import { ChangeDetectionStrategy, Component, computed, inject, input, linkedSignal, signal, ViewContainerRef } from '@angular/core';
import { provideTranslocoScope, TranslocoDirective } from '@jsverse/transloco';
import { SdsBanner } from '@nieuwestroom/sds-ui/banner';
import { SdsSwitch } from '@nieuwestroom/sds-ui/switch';
import { NgpDialogManager } from 'ng-primitives/dialog';

import { UsersProfileService } from '@customer-portal/api';
import { NsErrorCard } from '@customer-portal/indicators/error';
import { NsSpinner } from '@customer-portal/indicators/spinner';
import { AccountState } from '@customer-portal/shared/account/data-access';

import { CommunicationPreference, CommunicationPreferenceId, CommunicationPreferencesStore } from '../data-access';

export type CommunicationPreferencesColumns = 'one' | 'two';

@Component({
  selector: 'nscp-communication-preferences',
  imports: [TranslocoDirective, SdsBanner, SdsSwitch, NsSpinner, NsErrorCard],
  templateUrl: './communication-preferences.html',
  styleUrl: './communication-preferences.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  providers: [UsersProfileService, CommunicationPreferencesStore, provideTranslocoScope('accountDetails')],
})
export class CommunicationPreferences {
  protected readonly communicationPreferencesStore = inject(CommunicationPreferencesStore);
  private readonly accountState = inject(AccountState);

  /**
   * Number of columns for the individual preference items.
   * "Mijn gegevens" uses a single column; the onboarding step uses two (always one column on mobile).
   */
  public readonly columns = input<CommunicationPreferencesColumns>('one');

  /**
   * Copy shown by the load-error card. "Mijn gegevens" uses the generic core error; onboarding
   * points the user at retrying later via "Mijn gegevens" instead, since this step can be skipped.
   */
  public readonly loadingErrorSource = input<'default' | 'onboarding'>('default');

  protected readonly email = this.accountState.$email;
  protected readonly draftPreferences = linkedSignal<readonly CommunicationPreference[]>(() => this.clonePreferences(this.communicationPreferencesStore.preferences()));
  private readonly dialogManager = inject(NgpDialogManager);
  // ng-primitives otherwise falls back to the root component's ViewContainerRef, which is not
  // available in every host (e.g. TestBed). Mirrors page-content-company-details-general.
  private readonly viewContainerRef = inject(ViewContainerRef);

  public readonly isSaving = this.communicationPreferencesStore.isSaving;
  public readonly loadError = this.communicationPreferencesStore.loadError;
  public readonly lastSaveFailed = signal(false);
  public readonly allDraftPreferencesEnabled = computed(() => this.draftPreferences().length > 0 && this.draftPreferences().every((preference) => preference.enabled));
  public readonly hasChanges = computed(() => {
    const persistedPreferences = new Map(this.communicationPreferencesStore.preferences().map((preference) => [preference.id, preference.enabled]));
    return this.draftPreferences().some((preference) => persistedPreferences.get(preference.id) !== preference.enabled);
  });

  protected updatePreference(id: CommunicationPreferenceId, enabled: boolean): void {
    this.draftPreferences.update((preferences) => preferences.map((preference) => (preference.id === id ? { ...preference, enabled } : preference)));
  }

  protected updateAll(enabled: boolean): void {
    this.draftPreferences.update((preferences) => preferences.map((preference) => ({ ...preference, enabled })));
  }

  public async save(): Promise<boolean> {
    if (!this.hasChanges()) {
      return true;
    }

    const success = await this.communicationPreferencesStore.save(this.draftPreferences());
    if (!success) {
      this.draftPreferences.set(this.clonePreferences(this.communicationPreferencesStore.preferences()));
      this.lastSaveFailed.set(!success);
    }

    return success;
  }

  private clonePreferences(preferences: readonly CommunicationPreference[]): CommunicationPreference[] {
    return preferences.map((preference) => ({ ...preference }));
  }
}
